import React , {useLayoutEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact, {
	HighchartsReactRefObject,
} from 'highcharts-react-official';

interface ColumnDataPoint {
	x: number;
	volume: number;
	failureRate: number;
}

interface StackedColumnChartProps {
	title?: string;
	chargerSessionVolumeData?: IChargerSessionVolumeData[];
}

const StackedColumnChart: React.FC<StackedColumnChartProps> = ({
	chargerSessionVolumeData,
}) => {
	const chartRef = React.useRef<HighchartsReactRefObject>(null);
	const chartWrapRef = useRef<HTMLDivElement>(null);
	const chartSizeRef = useRef<{ width: number; height: number }>({
	width: 0,
	height: 200,
});

	// New daily data

	const sessionVolumeData = chargerSessionVolumeData || [];
	const len = sessionVolumeData.length;
	// Convert to the format expected by the chart
	const defaultData: ColumnDataPoint[] = sessionVolumeData.map(
		(item, index) => ({
			x: index,
			volume: Number(item.successfulSessions) + Number(item.failedSessions),
			failureRate: Number(item.failedSessions),
		})
	);

	const finalData = defaultData;

	// Calculate successful volume (green) and failed volume (red)
	const successData = finalData.map((d) => ({
		x: d.x,
		y:  (d.volume - d.failureRate),
	}));

	const failureData = finalData.map((d) => ({
		x: d.x,
		y: d.failureRate,
	}));

	const failureTrendData = finalData.map((d) => ({
		x: d.x,
		y: d.volume ? (d.failureRate /d.volume) * 100 : 0,
	}));


	const maxFailureRate = Math.max(
  0,
  ...failureTrendData.map(d => d.y)
);

	const roundedMax = Math.min(
	100,
	Math.ceil(maxFailureRate / 10) * 10
	);

	const options: Highcharts.Options = {
		chart: {
			type: 'column',
			backgroundColor: 'transparent',
			//height: 100,

			spacingLeft: 15,
		},

		title: {
			text: undefined,
		},
		credits: { enabled: false },
		exporting: { enabled: false },

		xAxis: {
			type: 'category',
			categories: sessionVolumeData.map((item) => item.date),
			labels: {
				style: {
					color: '#999',
					fontSize: '11px',
				},
				step: Math.ceil(len / 10),
			},
			lineColor: '#e5e5e5',
			tickColor: '#e5e5e5',
		},

		yAxis: [
			{
				title: {
					text: 'Session Volume',
					
					style: {
						color: '#4caf50',
						fontSize: '12px',
						fontWeight: '500',
					},
					// rotation: 270,
					align: 'middle',
					x: -4,
				},
				tickAmount: 3, 
				
				labels: {
					style: {
						color: '#666',
						fontSize: '11px',
					},
					
				},
				gridLineColor: '#f5f5f5',
			},
			
			{
				title: {
					text: 'Failure Rate (%)',
					style: {
						color: '#e53935',
						fontSize: '12px',
						fontWeight: '500',
					},
				
					x: -2,
				},
				tickAmount: 3, 
				labels: {
					style: {
						color: '#666',
						fontSize: '11px',
					},
				},
				opposite: true,
				gridLineWidth: 0,
				max: roundedMax
			
			
			},
		],

		legend: {
			enabled: false,
		},

		plotOptions: {
			column: {
				stacking: 'normal',
				borderWidth: 0,
				pointPadding: 0.1,
				groupPadding: 0.15,
				borderRadius: 3,
			},
		},

		tooltip: {
			shared: true,
			useHTML: true,
			borderWidth: 1,
			borderColor: '#e5e5e5',
			borderRadius: 6,
			backgroundColor: 'white',
			shadow: {
				color: 'rgba(0,0,0,0.1)',
				offsetX: 0,
				offsetY: 2,
				opacity: 0.15,
				width: 4,
			},
			padding: 10,
			style: {
				fontSize: '12px'
			},
			formatter: function () {
  const date = this.x;

  const failurePoint = this.points?.find(
    p => p.series.type === 'column' && p.series.name === 'Failure'
  );

  const successPoint = this.points?.find(
    p => p.series.type === 'column' && p.series.name === 'Success'
  );

  const trendPoint = this.points?.find(
    p => p.series.type === 'spline'
  );

  const total =
    (failurePoint?.y || 0) + (successPoint?.y || 0);

  let tooltipHTML = `
   
    <div style="margin-bottom:4px;">
      <b>Total Session Volume:</b> ${total.toFixed(0)}
    </div>
  `;

  // Failure with percentage
  if (failurePoint) {
    tooltipHTML += `
      <div style="margin-bottom:2px;">
        <b>Failure:</b> ${failurePoint.y?.toFixed(0)}
        ${
          trendPoint
            ? ` <span style="color:#777;">(${trendPoint.y?.toFixed(1)}%)</span>`
            : ''
        }
		
      </div>
    `;
  }

  // Success
  if (successPoint) {
    tooltipHTML += `
	  <div>
      <div style="margin-bottom:2px;">
        <b>Success:</b> ${successPoint.y?.toFixed(0)}
      </div>
	   <div style="font-weight:500;margin-top:4px; color:#7C8D8F">${date}</div>
	   </div>
    `;
  }

  return tooltipHTML;
},

		},

		series: [
		{
				type: 'column',
				name: 'Failure',
				data: failureData,
				yAxis: 0,
				color: '#ef5b59ff',
				stack: 'volume',
				borderRadius: 8,
				pointPadding: 0.2,
				groupPadding: 0.1,
			},
	
			{
				type: 'column',
				name: 'Success',
				data: successData,
				yAxis: 0,
				color: '#6ab46cff',
				stack: 'volume',
				borderRadius: 8,
				pointPadding: 0.2,
				groupPadding: 0.1,
			},
			
			{
				type: 'spline',
				name: 'Failure Trend',
				data: failureTrendData,
				yAxis: 1,
				color: '#ef5b59ff',
				lineWidth: 2,
				marker: {
					enabled: false,
				},
				dashStyle: 'Dot',
				
			}


		],
	};

	useLayoutEffect(() => {
	if (!chartWrapRef.current) return;

	let rafId: number | null = null;

	const observer = new ResizeObserver(entries => {
		const { width, height } = entries[0].contentRect;

		const newWidth = Math.floor(width);
		const newHeight = Math.floor(height);

		const prev = chartSizeRef.current;
		if (prev.width === newWidth && prev.height === newHeight) return;

		chartSizeRef.current = {
			width: newWidth,
			height: newHeight,
		};

		// Break ResizeObserver → layout loop
		if (rafId) cancelAnimationFrame(rafId);

		rafId = requestAnimationFrame(() => {
			const chart = chartRef.current?.chart;
			if (chart) {
				chart.setSize(newWidth, newHeight, false);
			}
		});
	});

	observer.observe(chartWrapRef.current);

	return () => {
		if (rafId) cancelAnimationFrame(rafId);
		observer.disconnect();
	};
}, []);



	return (
		<div className='session-volume-trend-graph' ref={chartWrapRef}>
			<HighchartsReact
				highcharts={Highcharts}
				options={options}
				immutable={false}
				allowChartUpdate={true}
				updateArgs={[true, true, true]}
				ref={chartRef}
			/>
		</div>
	);
};

export default StackedColumnChart;
